import React, { useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const SENTIMENT_COLORS = {
  positive: { label: "Positive", color: "#14B882" },
  neutral:  { label: "Neutral",  color: "#ABABBA" },
  negative: { label: "Negative", color: "#E50026" },
};

const SENTIMENT_KEYS = Object.keys(SENTIMENT_COLORS);

const Y_TICKS = [0, 25, 50, 75, 100];

const IconChartBar = ({ size = 16, active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? "#1C1B22" : "#ABABBA"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6" />
    <path d="M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10" />
    <path d="M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14" />
    <path d="M4 20h14" />
  </svg>
);

const IconChartLine = ({ size = 16, active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? "#1C1B22" : "#ABABBA"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M4 19l16 0" />
    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
  </svg>
);

const SentimentScoreChart = ({ data, height = 360, onViewMore }) => {
  const [chartType, setChartType] = useState("line");
  const [hiddenSentiments, setHiddenSentiments] = useState(new Set());

  const toggleSentiment = (key) => {
    setHiddenSentiments((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle flex flex-col" style={{ height }}>
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Sentiment trends</h3>
          <TooltipUI
            content="Tracks how sentiment changes over time (positive / neutral / negative shares by day)."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No sentiment data available"
          description="There's no sentiment data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return `${date.toLocaleString('default', { month: 'short' })} ${day}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const [year, month, day] = label.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });

      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium mb-1">{formattedDate}</p>
          {payload
            .filter((entry) => !hiddenSentiments.has(entry.dataKey))
            .sort((a, b) => b.value - a.value)
            .map((entry) => (
              <div key={entry.dataKey} className="flex items-center gap-2 text-sm py-0.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-grey-60">{SENTIMENT_COLORS[entry.dataKey]?.label}:</span>
                <span className="font-medium text-black-0">{Math.round(entry.value)}%</span>
              </div>
            ))}
        </div>
      );
    }
    return null;
  };

  const renderLegend = () => (
    <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-1">
      {SENTIMENT_KEYS.map((key) => {
        const hidden = hiddenSentiments.has(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggleSentiment(key)}
            className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
            style={{ opacity: hidden ? 0.35 : 1 }}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: SENTIMENT_COLORS[key].color }}
            />
            <span className={hidden ? "text-grey-60 line-through" : "text-grey-60"}>
              {SENTIMENT_COLORS[key].label}
            </span>
          </button>
        );
      })}
    </div>
  );

  const visibleKeys = SENTIMENT_KEYS.filter((k) => !hiddenSentiments.has(k));

  const sharedXAxis = (
    <XAxis
      dataKey="date"
      tickFormatter={formatDate}
      tick={{ fontSize: 12 }}
      tickMargin={10}
      stroke="#ABABBA"
    />
  );

  const sharedYAxis = (
    <YAxis
      domain={[0, 100]}
      ticks={Y_TICKS}
      tickFormatter={(v) => `${v}%`}
      tick={{ fontSize: 12 }}
      tickMargin={10}
      stroke="#ABABBA"
      allowDataOverflow={false}
    />
  );

  return (
    <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle flex flex-col" style={{ height }}>
      <div className="pl-5 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Sentiment trends</h3>
          <TooltipUI
            content="Tracks how sentiment changes over time (positive / neutral / negative shares by day)."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>

        <div className="flex items-center gap-3">
          {typeof onViewMore === "function" && (
            <button
              type="button"
              onClick={onViewMore}
              className="text-xs text-grey-60 hover:text-brand hover:underline"
            >
              View more
            </button>
          )}
          <div className="bg-grey-10 flex items-center p-[2px] rounded-[10px] gap-[2px]">
          <button
            type="button"
            onClick={() => setChartType("line")}
            title="Line chart"
            className={`h-[28px] flex items-center justify-center px-2 py-1 rounded-[8px] transition-all ${
              chartType === "line"
                ? "bg-white-f border border-grey-20 shadow-[0px_1px_1px_0px_rgba(28,27,34,0.1)]"
                : ""
            }`}
          >
            <IconChartLine active={chartType === "line"} />
          </button>
          <button
            type="button"
            onClick={() => setChartType("bar")}
            title="Stacked bar chart"
            className={`h-[28px] flex items-center justify-center px-2 py-1 rounded-[8px] transition-all ${
              chartType === "bar"
                ? "bg-white-f border border-grey-20 shadow-[0px_1px_1px_0px_rgba(28,27,34,0.1)]"
                : ""
            }`}
          >
            <IconChartBar active={chartType === "bar"} />
          </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
              {sharedXAxis}
              {sharedYAxis}
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#ABABBA", strokeWidth: 1 }} />
              <Legend content={renderLegend} />
              {visibleKeys.map((key) => (
                <Line
                  key={key}
                  type="linear"
                  dataKey={key}
                  stroke={SENTIMENT_COLORS[key].color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={true}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
              {sharedXAxis}
              {sharedYAxis}
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
              <Legend content={renderLegend} />
              {SENTIMENT_KEYS.map((key) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="sentiment"
                  fill={SENTIMENT_COLORS[key].color}
                  hide={hiddenSentiments.has(key)}
                  isAnimationActive={true}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SentimentScoreChart;
